import { observer } from "mobx-react-lite";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ProposalPricingWithServiceNames } from "./ProposalSectionPricingStore";
import { ProposalStep3AllAttributesDto } from "@gap/backend/app-services/proposal/models/ProposalStep3Dto";
import useStore from "reusable/mobx-store-utils";
import ProposalSectionPricingSchemeSelectorStore, { ProposalSectionPricingSchemeSelectorStoreProvider, useProposalSectionPricingSchemeSelectorStore } from "./ProposalSectionPricingSchemeSelectorStore";
import { useProposalsViewProposalStore } from "../../ProposalsViewProposalStore";
import { PaymentMethodOptionValue } from "@gap/backend/app-services/services/models/ServiceRateTypesDto";
import { themeConfig } from '@gap/shared/theme';
import { ulid } from "ulid";

export default observer(function ProposalSectionPricingSchemeSelector({ pricingItem: minimalItem, showPreviewHints }: ProposalSectionPricingSchemeSelectorProps) {
   const proposalsViewProposalStore = useProposalsViewProposalStore()
   const pricingItem = minimalItem as unknown as ProposalStep3AllAttributesDto
   const proposalSectionPricingSchemeSelectorStore = useStore((sp) => new ProposalSectionPricingSchemeSelectorStore(sp), { pricingItem, proposalsViewProposalStore })

   return <ProposalSectionPricingSchemeSelectorStoreProvider store={proposalSectionPricingSchemeSelectorStore}>
      <Stack sx={{ gap: 3 }}>
         <Stack sx={{ gap: .5 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Choose your preferred way to pay</Typography>
            {showPreviewHints
               ? <Typography variant="body2" sx={{ color: 'secondary.light' }}>Your client will be able to choose one option from any of the following payment options you enable for this fee type.</Typography>
               : undefined}
         </Stack>
         <PaymentRadioGroup {...{ pricingItem }} />
      </Stack>
   </ProposalSectionPricingSchemeSelectorStoreProvider>
})

const PaymentRadioGroup = observer(function PaymentRadioGroup({ pricingItem }: { pricingItem: ProposalStep3AllAttributesDto }) {
   const proposalSectionPricingSchemeSelectorStore = useProposalSectionPricingSchemeSelectorStore()
   const { paymentPreferences } = proposalSectionPricingSchemeSelectorStore.sp.proposalsViewProposalStore

   function onChange(event: React.ChangeEvent<HTMLInputElement>) {
      paymentPreferences[pricingItem.serviceRateType] = event.target.value as PaymentMethodOptionValue
   }

   const radioGroupName = `payment-method-${pricingItem.serviceRateType}-${ulid()}`

   return <RadioGroup
      name={radioGroupName}
      value={paymentPreferences[pricingItem.serviceRateType]}
      onChange={onChange}
      sx={{ gap: 1 }}
   >
      {proposalSectionPricingSchemeSelectorStore.options.map((props) =>
         <PaymentRadioOption {...props} key={props.value} />)
      }
   </RadioGroup>
})

const PaymentRadioOption = observer(function PaymentRadioOption({ value, label, caption, disabled }: PaymentRadioOptionProps) {

   return <FormControlLabel
      control={<Radio />}
      sx={{
         borderRadius: '16px',
         border: `1px solid ${themeConfig.primary[200]}`,
         mx: 0,
         px: 1,
      }}
      label={
         <Stack sx={{ paddingY: 2 }}>
            <Typography variant="body1">{label}</Typography>
            {caption && <Typography variant="caption" sx={{ color: 'secondary.light' }}>{caption}</Typography>}
         </Stack>
      }
      {...{ value, disabled }}
   />
})

export interface PaymentRadioOptionProps {
   value: PaymentMethodOptionValue,
   label: string,
   caption?: string,
   disabled?: boolean,
}

interface ProposalSectionPricingSchemeSelectorProps {
   pricingItem: ProposalPricingWithServiceNames,
   /** There are some nuances if this is an advisor previewing their proposal */
   showPreviewHints?: boolean,
}
